import { FACTSET_SCOPES } from "./factsetConstants";
import {
  getFactSetOAuthSession,
  type FactSetOAuthSessionData,
} from "./factsetOAuthClient";
import { forceReconnectFactSetOAuth } from "./factsetReconnect";
import { registerFactSetClientSessionClearHandler } from "./factsetSessionLifecycle";

export type ChartCreatorMode = "api" | "legacy";

export interface ChartCreatorModeResolution {
  mode: ChartCreatorMode;
  session?: FactSetOAuthSessionData;
  message?: string;
  unsupported?: boolean;
}

export interface ResolveChartCreatorModeOptions {
  log?: (message: string) => void;
  onReauthGranted?: () => void;
}

interface ReauthFlight {
  generation: number;
  promise: Promise<void>;
}

let sessionGeneration = 0;
let reauthAttempted = false;
let reauthFlight: ReauthFlight | null = null;

function clearChartCreatorSessionState(): void {
  sessionGeneration += 1;
  reauthAttempted = false;
  reauthFlight = null;
}

registerFactSetClientSessionClearHandler(clearChartCreatorSessionState);

function sessionHasOfficeTools(session: FactSetOAuthSessionData): boolean {
  return (
    session.hasOfficeToolsAccess === true ||
    (session.scopes ?? []).includes(FACTSET_SCOPES.OFFICE_TOOLS)
  );
}

export function runOrJoinChartCreatorReauth(): Promise<void> {
  const generation = sessionGeneration;
  if (reauthFlight?.generation === generation) return reauthFlight.promise;
  reauthAttempted = true;
  const promise = forceReconnectFactSetOAuth();
  const flight: ReauthFlight = {
    generation,
    promise: promise.finally(() => {
      if (
        sessionGeneration === generation &&
        reauthFlight?.generation === generation &&
        reauthFlight.promise === flight.promise
      ) {
        reauthFlight = null;
      }
    }),
  };
  reauthFlight = flight;
  return flight.promise;
}

export async function resolveChartCreatorMode(
  options: ResolveChartCreatorModeOptions = {},
): Promise<ChartCreatorModeResolution> {
  const generation = sessionGeneration;
  const { log, onReauthGranted } = options;
  try {
    let session = await getFactSetOAuthSession();
    if (generation !== sessionGeneration) return resolveChartCreatorMode(options);
    if (!session.connected) {
      log?.("FactSet OAuth is not connected; using legacy chart intents.");
      return { mode: "legacy", session };
    }
    if (sessionHasOfficeTools(session)) {
      return { mode: "api", session };
    }
    if (!reauthAttempted || reauthFlight?.generation === generation) {
      try {
        await runOrJoinChartCreatorReauth();
        if (generation !== sessionGeneration) return resolveChartCreatorMode(options);
        session = await getFactSetOAuthSession();
        if (generation !== sessionGeneration) return resolveChartCreatorMode(options);
        if (sessionHasOfficeTools(session)) {
          onReauthGranted?.();
          return { mode: "api", session };
        }
      } catch (error) {
        log?.(
          `FactSet Chart Creator reconnect did not complete; using legacy intents: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }
    }
    return { mode: "legacy", session };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const unsupported =
      message.includes("standalone SSO") ||
      message.includes("unsupported_mode") ||
      message.includes("not configured");
    return { mode: "legacy", message, unsupported };
  }
}
